if (!customElements.get('olivers-hero-gallery')) {
  class OliversHeroGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.initializeFrame = null;
      this.transitioning = false;
      this.queuedIndex = null;
      this.zoomAnimations = new Map();
      this.fadeAnimations = new Set();
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.initializeFrame = window.requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(3000, Number(this.dataset.interval || 3000));
      this.fadeDuration = 1000;

      if (this.slides.length < 2) return;

      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.classList.toggle('is-active', active);
        slide.style.opacity = active ? '1' : '0';
        slide.style.zIndex = active ? '2' : '0';
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          this.showSlide(Number(dot.dataset.galleryDot));
        });
      });

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.dataset.galleryReady = 'true';
      this.startZoom(this.slides[this.currentIndex], this.interval);
      this.schedule();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.initializeFrame);
      window.clearTimeout(this.timer);
      this.zoomAnimations.forEach(animation => animation.cancel());
      this.fadeAnimations.forEach(animation => animation.cancel());
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    async showSlide(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
      if (index === this.currentIndex) return;
      if (this.transitioning) {
        this.queuedIndex = index;
        return;
      }

      window.clearTimeout(this.timer);
      this.transitioning = true;
      const previousSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];
      this.currentIndex = index;

      previousSlide.setAttribute('aria-hidden', 'true');
      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');
      previousSlide.style.zIndex = '1';
      previousSlide.style.opacity = '1';
      nextSlide.style.zIndex = '2';
      nextSlide.style.opacity = '0';

      this.startZoom(nextSlide, this.interval + this.fadeDuration);

      const animationOptions = {
        duration: this.fadeDuration,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards'
      };
      const fadeOut = previousSlide.animate([{ opacity: 1 }, { opacity: 0 }], animationOptions);
      const fadeIn = nextSlide.animate([{ opacity: 0 }, { opacity: 1 }], animationOptions);
      this.fadeAnimations.add(fadeOut);
      this.fadeAnimations.add(fadeIn);

      await Promise.allSettled([fadeOut.finished, fadeIn.finished]);
      if (!this.isConnected) return;

      previousSlide.style.opacity = '0';
      previousSlide.style.zIndex = '0';
      previousSlide.classList.remove('is-active');
      nextSlide.style.opacity = '1';
      nextSlide.style.zIndex = '2';
      fadeOut.cancel();
      fadeIn.cancel();
      this.fadeAnimations.delete(fadeOut);
      this.fadeAnimations.delete(fadeIn);

      const previousZoom = this.zoomAnimations.get(previousSlide);
      if (previousZoom) previousZoom.cancel();
      this.zoomAnimations.delete(previousSlide);
      const previousImage = previousSlide.querySelector('.olivers-hero__image');
      if (previousImage) previousImage.style.transform = 'scale(1)';

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.transitioning = false;
      if (this.queuedIndex !== null && this.queuedIndex !== this.currentIndex) {
        const queuedIndex = this.queuedIndex;
        this.queuedIndex = null;
        this.showSlide(queuedIndex);
        return;
      }
      this.queuedIndex = null;
      this.schedule();
    }

    next() {
      this.showSlide((this.currentIndex + 1) % this.slides.length);
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (!this.autoplay || this.transitioning || document.hidden) return;
      this.timer = window.setTimeout(() => this.next(), this.interval);
    }

    startZoom(slide, duration) {
      const image = slide?.querySelector('.olivers-hero__image');
      if (!image) return;

      const existingAnimation = this.zoomAnimations.get(slide);
      if (existingAnimation) existingAnimation.cancel();
      image.style.transform = 'scale(1)';

      const zoomAnimation = image.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.065)' }],
        {
          duration: Math.max(1200, duration),
          easing: 'linear',
          fill: 'forwards'
        }
      );
      this.zoomAnimations.set(slide, zoomAnimation);
      zoomAnimation.finished.catch(() => {});
    }

    onVisibilityChange() {
      if (document.hidden) {
        window.clearTimeout(this.timer);
        this.zoomAnimations.forEach(animation => animation.pause());
        this.fadeAnimations.forEach(animation => animation.pause());
      } else {
        this.zoomAnimations.forEach(animation => animation.play());
        this.fadeAnimations.forEach(animation => animation.play());
        this.schedule();
      }
    }
  }

  customElements.define('olivers-hero-gallery', OliversHeroGallery);
}

if (!customElements.get('olivers-market-card')) {
  class OliversMarketCard extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-market-list]');
      this.limit = Math.max(1, Number(this.dataset.limit || 3));
      this.marketLink = this.dataset.marketLink || '';

      try {
        const dataNode = this.querySelector('[data-market-locations]');
        this.locations = JSON.parse(dataNode?.textContent || '[]');
      } catch (error) {
        this.locations = [];
        console.error('Markttermine konnten nicht gelesen werden.', error);
      }

      this.render();
    }

    render() {
      const availability = window.OliversPickupAvailability;
      if (!availability || !this.list) return;

      const markets = this.locations
        .filter(location => location.active === true)
        .map(location => {
          const date = availability.availableDates(location, { limit: 1 })[0];
          return date ? { ...location, date } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.date.localeCompare(b.date) || Number(a.sortOrder || 999) - Number(b.sortOrder || 999))
        .slice(0, this.limit);

      if (markets.length === 0) {
        this.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      markets.forEach(market => fragment.append(this.createRow(market, availability)));
      this.list.replaceChildren(fragment);
      this.hidden = false;
    }

    createRow(market, availability) {
      const row = document.createElement(this.marketLink ? 'a' : 'div');
      row.className = 'olivers-hero__market-row';
      if (this.marketLink) row.href = this.marketLink;

      const details = document.createElement('span');
      const name = document.createElement('strong');
      name.textContent = market.name || '';
      details.append(name);

      if (market.hours) {
        const hours = document.createElement('small');
        hours.textContent = market.hours;
        details.append(hours);
      }

      const date = document.createElement('span');
      date.className = 'olivers-hero__market-date';
      date.textContent = availability.formatCompactDate(market.date);

      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';

      row.append(details, date, arrow);
      return row;
    }
  }

  customElements.define('olivers-market-card', OliversMarketCard);
}
